import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { StreamEvent, StreamEventType } from './stream.interfaces';
import { StreamChunkData, UniversalToolCall } from '@app-libs/model';
import { ChatStateService } from '../chat-state.service';
import { EventBusService } from '../../client/event-bus.service';
import { CitationProcessor } from '../utils/citation-processor.service';
import { normalizeToolCall } from '../../../utils/tool-transform.utils';

/**
 * StreamProcessor - Handles chunk processing and state updates
 *
 * This service handles:
 * - Stream chunk processing and content accumulation
 * - State updates during streaming
 * - Error handling during streaming
 * - Event emission for stream events
 * - Tool execution tracking during streaming
 * 
 * Tool Event Flow:
 * 1. tool_use: Tool detected, update status to 'executing'
 * 2. tool_calls: Tools being executed (informational)
 * 3. tool_end: Execution complete, update status to 'completed'/'failed'
 * 
 * The service maintains tool call state at the message level and preserves
 * the streaming_tool_status through the entire message lifecycle.
 */
@Injectable({
  providedIn: 'root',
})
export class StreamProcessor {
  private streamEventSubject = new Subject<StreamEvent>();
  public streamEvent$ = this.streamEventSubject.asObservable();

  // Track if this is the first chunk
  private isFirstChunk = true;
  
  // Track current message index
  private currentMessageIndex = -1;
  
  // Track tool calls for current stream
  private currentToolCalls: UniversalToolCall[] = [];

  constructor(
    private chatStateService: ChatStateService,
    private eventBus: EventBusService,
    private citationProcessor: CitationProcessor
  ) {}

  /**
   * Initialize processor for a new stream
   */
  initializeStream(messageIndex: number): void {
    this.isFirstChunk = true;
    this.currentMessageIndex = messageIndex;
    this.chatStateService.currentBotMessage = ''; // Reset accumulator
    this.currentToolCalls = []; // Reset tool calls
    
    // Set streaming state to true
    this.chatStateService.isStreaming = true;
  }

  /**
   * Process streaming chunk
   */
  processChunk(chunk: StreamChunkData): void {
    // Handle tool-related chunks
    if (chunk.type === 'tool_use') {
      this.handleToolStart(chunk);
      return;
    }
    
    if (chunk.type === 'tool_calls') {
      // If tool_calls are provided in the chunk, normalize them
      if (chunk.tool_calls && Array.isArray(chunk.tool_calls)) {
        const provider = chunk.metadata?.['provider'] || 'openai';
        this.currentToolCalls = chunk.tool_calls.map(tool => 
          normalizeToolCall(tool, provider)
        );
      }
      this.handleToolExecuting(chunk);
      return;
    }
    
    if (chunk.type === 'tool_end') {
      this.handleToolEnd(chunk);
      return;
    }
    
    // Emit first chunk event
    if (this.isFirstChunk) {
      this.eventBus.emitStreamFirstChunkReceived(chunk);
      this.isFirstChunk = false;
    }

    // Log usage if available - StreamChunkData has usage in data.usage or data.data.usage
    const usage = chunk.data?.usage || chunk.data?.data?.usage;
    if (usage) {
      console.debug('Stream token usage:', usage);
    }

    // Tool calls are now handled entirely by the backend
    // Frontend only needs to display the final response

    // Strip citations and accumulate content
    const content = this.citationProcessor.stripCitations(chunk.content || '');
    this.chatStateService.appendToBotMessage(content);

    // Update streaming message in state
    if (this.currentMessageIndex >= 0) {
      this.chatStateService.updateStreamingMessage(
        this.currentMessageIndex,
        this.chatStateService.currentBotMessage
      );
    }

    // Emit chunk event
    this.emitStreamEvent({
      type: StreamEventType.CHUNK_RECEIVED,
      data: chunk,
      timestamp: new Date()
    });
  }

  /**
   * Handle stream start
   */
  handleStreamStart(): void {
    this.emitStreamEvent({
      type: StreamEventType.STREAM_START,
      timestamp: new Date()
    });
  }

  /**
   * Handle stream completion
   * 
   * This method is called when the entire stream completes.
   * It must preserve any tool status that was set during streaming.
   */
  handleStreamComplete(): void {
    // Get final content
    const finalContent = this.chatStateService.currentBotMessage;
    
    // Update final tool calls but preserve the completed/failed status
    if (this.currentMessageIndex >= 0 && this.currentToolCalls.length > 0) {
      const currentMessage = this.chatStateService.messages[this.currentMessageIndex];
      const existingMetadata = currentMessage?.metadata || {};
      const existingStatus = existingMetadata['streaming_tool_status'];
      
      // Build metadata update object by merging with existing metadata
      // This ensures we don't lose any existing metadata fields
      const metadataUpdate: any = { 
        ...existingMetadata,
        tool_calls: [...this.currentToolCalls]
      };
      
      // IMPORTANT: Preserve the tool status if it's already been set to completed/failed
      // This ensures the UI continues to show the correct tool status after streaming completes
      if (existingStatus && existingStatus !== 'executing') {
        metadataUpdate.streaming_tool_status = existingStatus;
      }
      
      this.chatStateService.updateMessageByIndex(this.currentMessageIndex, {
        metadata: metadataUpdate
      });
    }
    
    // Emit completion event
    this.emitStreamEvent({
      type: StreamEventType.STREAM_COMPLETE,
      data: { content: finalContent },
      timestamp: new Date()
    });

    // Reset state
    this.isFirstChunk = true;
    this.currentMessageIndex = -1;
    
    // Set streaming state to false
    this.chatStateService.isStreaming = false;
  }

  /**
   * Handle stream error
   */
  handleStreamError(error: Error): void {
    console.error('StreamProcessor: Stream error:', error);
    
    // Update message with error if we have an index
    if (this.currentMessageIndex >= 0) {
      this.chatStateService.updateMessageByIndex(this.currentMessageIndex, {
        content: `*${error.message || error}*`,
      });
    }

    // Emit error event
    this.emitStreamEvent({
      type: StreamEventType.STREAM_ERROR,
      error: error,
      timestamp: new Date()
    });

    // Reset state
    this.isFirstChunk = true;
    this.currentMessageIndex = -1;
    
    // Set streaming state to false
    this.chatStateService.isStreaming = false;
  }

  /**
   * Emit stream event
   */
  emitStreamEvent(event: StreamEvent): void {
    this.streamEventSubject.next(event);
  }

  /**
   * Get the current accumulated message content
   */
  getCurrentContent(): string {
    return this.chatStateService.currentBotMessage;
  }
  
  /**
   * Get the tool calls for the current stream
   */
  getCurrentToolCalls(): UniversalToolCall[] {
    console.debug('StreamProcessor: Returning tool calls:', this.currentToolCalls);
    return this.currentToolCalls;
  }

  /**
   * Handle tool start event
   */
  private handleToolStart(chunk: StreamChunkData): void {
    // Get provider from metadata for proper normalization
    const provider = chunk.metadata?.['provider'] || 'openai'; // Default to openai for backward compatibility
    
    // Create normalized tool call
    if (chunk.metadata?.['toolName']) {
      const normalizedTool: UniversalToolCall = {
        id: chunk.metadata?.['toolId'] || `tool_${Date.now()}`,
        name: chunk.metadata['toolName'],
        arguments: {}, // Will be populated later
        provider_metadata: { provider }
      };
      
      this.currentToolCalls.push(normalizedTool);
      
      // Update message only once with streaming status
      if (this.currentMessageIndex >= 0 && this.currentToolCalls.length === 1) {
        // Only update on first tool to reduce state updates
        const currentMessage = this.chatStateService.messages[this.currentMessageIndex];
        const existingMetadata = currentMessage?.metadata || {};
        
        this.chatStateService.updateMessageByIndex(this.currentMessageIndex, {
          metadata: { 
            ...existingMetadata,
            streaming_tool_status: 'executing'
          }
        });
      }
    }
    
    // Emit tool started event
    this.emitStreamEvent({
      type: StreamEventType.TOOL_STARTED,
      data: chunk.metadata,
      timestamp: new Date()
    });
    
    console.debug('Tool started:', chunk.metadata);
  }
  
  /**
   * Handle tool executing event
   */
  private handleToolExecuting(chunk: StreamChunkData): void {
    // Emit tool executing event
    this.emitStreamEvent({
      type: StreamEventType.TOOL_EXECUTING,
      data: chunk.metadata,
      timestamp: new Date()
    });
    
    console.debug('Tools executing:', chunk.metadata);
  }
  
  /**
   * Handle tool end event
   */
  private handleToolEnd(chunk: StreamChunkData): void {
    // Update message to show tools completed (not executing anymore)
    if (this.currentMessageIndex >= 0) {
      const currentMessage = this.chatStateService.messages[this.currentMessageIndex];
      const existingMetadata = currentMessage?.metadata || {};
      
      this.chatStateService.updateMessageByIndex(this.currentMessageIndex, {
        metadata: { 
          ...existingMetadata,
          tool_calls: [...this.currentToolCalls],
          streaming_tool_status: chunk.metadata?.['status'] === 'completed' ? 'completed' : 'failed'
        }
      });
    }
    
    // Emit event
    const eventType = chunk.metadata?.['status'] === 'completed' 
      ? StreamEventType.TOOL_COMPLETED 
      : StreamEventType.TOOL_FAILED;
      
    this.emitStreamEvent({
      type: eventType,
      data: chunk.metadata,
      timestamp: new Date()
    });
    
    console.debug('Tool completed:', chunk.metadata);
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.streamEventSubject.complete();
  }
}
